import { PropsWithChildren } from 'react';
import { NumberFieldProps } from 'react-aria-components';

import { merge } from '@/components';
import {
  Badge,
  BasicTooltip,
  Button,
  Flex,
  FlexProps,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  NumberField,
  Popover,
  Select,
  SelectProps,
  SelectValue,
  Slider,
  SliderProps,
  Switch,
  SwitchProps,
  Text,
} from '@/components/core';
import { ChevronIcon, ChevronSelectorIcon } from '@/components/icons';
import { toNumber } from '@/utils';

type OptionProps = FlexProps;

const Option = ({ children, className, ...rest }: OptionProps) => {
  return (
    <Flex {...rest} className={merge('w-full items-center', className)}>
      {children}
    </Flex>
  );
};

export type OptionLabelProps = {
  label: string;
  description?: string;
  tooltip?: string;
};

export const OptionLabel = ({ label, description, tooltip }: OptionLabelProps) => {
  return (
    <>
      <Flex className="gap-xs grow justify-start">
        <Label>{label}</Label>
        {tooltip && (
          <BasicTooltip>
            <Text>{tooltip}</Text>
          </BasicTooltip>
        )}
      </Flex>
      {description && (
        <Text variant="tertiary" slot="description">
          {description}
        </Text>
      )}
    </>
  );
};
export function NumericOption({
  label,
  description,
  tooltip,
  defaultValue,
  value,
  onChange,
  ...rest
}: OptionLabelProps & Pick<NumberFieldProps, 'value' | 'onChange' | 'defaultValue'>) {
  return (
    <Option {...rest}>
      <OptionLabel label={label} tooltip={tooltip} description={description} />

      <NumberField minValue={0} value={value} className="max-w-16" onChange={onChange} defaultValue={defaultValue}>
        <div className="relative max-w-full">
          <Input placeholder="2" className="inset-0 !w-full" />

          <Flex col className="px-xs absolute inset-y-0 right-0 h-full justify-center">
            <Button slot="increment" className="m-0 p-0">
              <ChevronIcon rotation="up" className="!size-3" />
            </Button>
            <Button slot="decrement" className="m-0 p-0">
              <ChevronIcon rotation="down" className="!size-3" />
            </Button>
          </Flex>
        </div>
      </NumberField>
    </Option>
  );
}

interface SelectItem<T> {
  id: string;
  name?: string;
  value?: T;
  description?: string;
  icon?: React.ReactNode;
  isDisabled?: boolean;
  isDefault?: boolean;
}

export interface SelectOptionProps<T> extends SelectProps, OptionLabelProps {
  items: Iterable<SelectItem<T>>;
}

export function SelectOption<T>({
  items,
  defaultSelectedKey,
  label,
  description,
  tooltip,
  col,
  ...rest
}: SelectOptionProps<T> & OptionProps) {
  return (
    <Option {...rest} col={col}>
      <OptionLabel label={label} tooltip={tooltip} description={description} />

      <Select defaultSelectedKey={defaultSelectedKey} className={col ? 'w-full' : ''}>
        <Button variant="secondary">
          <SelectValue>
            {({ selectedItem }) => {
              const item = selectedItem as SelectItem<T>;
              return (
                <Flex className="gap-sm w-full items-center" variant="plain">
                  {item?.icon && <span>{item.icon}</span>}
                  <Text className="grow text-start">{item?.name}</Text>

                  {item?.isDefault ? (
                    <Badge variant="primary" borderless>
                      <Text>Default</Text>
                    </Badge>
                  ) : null}
                </Flex>
              );
            }}
          </SelectValue>
          <ChevronSelectorIcon />
        </Button>

        <Popover>
          <ListBox items={items}>
            {(option) => (
              <ListBoxItem id={option.id} key={option.id} isDisabled={option.isDisabled}>
                <Flex className="gap-sm w-full items-center">
                  {option.icon ?? null}
                  <Text className="grow aria-disabled:text-gray-400" aria-disabled={option.isDisabled}>
                    {option?.name}
                  </Text>

                  {option.isDefault ? (
                    <Badge variant="primary" className="ml-sm" borderless>
                      <Text>Default</Text>
                    </Badge>
                  ) : null}
                </Flex>
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </Select>
    </Option>
  );
}

type BooleanOptionProps = OptionLabelProps & Omit<OptionProps, 'onChange'> & SwitchProps;

export const BooleanOption = ({ onChange, isSelected, col, ...rest }: BooleanOptionProps) => {
  return (
    <Option>
      <Flex className="w-full">
        <Flex className="grow" col={col}>
          <OptionLabel {...rest} />
        </Flex>
        <Switch onChange={onChange} isSelected={isSelected} />
      </Flex>
    </Option>
  );
};

type SliderOptionProps = OptionLabelProps & SliderProps;

export const SliderOption = ({ minValue, maxValue, step, value, onChange, ...rest }: SliderOptionProps) => {
  return (
    <Option className="gap-md items-start" col>
      <Flex className="w-full">
        <OptionLabel {...rest} />
        <span className="grow" />
        <span className="px-sm flex items-center rounded-xs border-1 border-gray-200 text-sm">
          {toNumber(value)?.toFixed(2)}
        </span>
      </Flex>

      <Slider
        className="w-full"
        minValue={minValue ?? 0}
        maxValue={maxValue ?? 1}
        value={value}
        step={step ?? 0.01}
        onChange={onChange}
      />
    </Option>
  );
};

export const OptionGroup = ({ children, className }: FlexProps) => (
  <Flex col className={merge('gap-lg', className)}>
    {children}
  </Flex>
);
